import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User as UserIcon, Mail, KeyRound, Sparkles, ArrowRight, Save, Loader2, Crown, Users, Phone, MapPin, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Account = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", picture: "",
    first_name: "", last_name: "",
    phone: "", address: "", cap: "", city: "",
  });
  const [saving, setSaving] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        picture: user.picture || "",
        first_name: user.first_name || "",
        last_name:  user.last_name  || "",
        phone:      user.phone      || "",
        address:    user.address    || "",
        cap:        user.cap        || "",
        city:       user.city       || "",
      });
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users/me/subscription");
        setSubscription(r.data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!user) return null;

  // Which fields the user still hasn't filled in — surfaced as a friendly
  // banner at the top so people know exactly what's outstanding. Same
  // required set as the Google-auth completion modal.
  const REQUIRED = ["first_name", "last_name", "phone", "address", "cap", "city"];
  const missingFields = REQUIRED.filter((k) => !(form[k] && form[k].trim()));

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      // Compose display name from first+last if the user filled them in but
      // hasn't customized the display-name field.
      const payload = { ...form };
      if (!payload.name?.trim() && (payload.first_name || payload.last_name)) {
        payload.name = `${payload.first_name || ""} ${payload.last_name || ""}`.trim();
      }
      const r = await api.put("/users/me/profile", payload);
      setUser({ ...user, ...r.data });
      toast.success("Profilo aggiornato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (newPw.length < 8) { toast.error("Nuova password minimo 8 caratteri"); return; }
    setPwSaving(true);
    try {
      await api.put("/users/me/password", { current_password: curPw, new_password: newPw });
      toast.success("Password aggiornata");
      setCurPw(""); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setPwSaving(false); }
  };

  const statusBadge = user.role === "admin"
    ? { label: "Super Admin", cls: "bg-[#12466F] text-white" }
    : (user.status === "subscriber"
        ? { label: "Abbonato", cls: "bg-emerald-500 text-white" }
        : { label: "Demo", cls: "bg-slate-500 text-white" });

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="ios-card overflow-hidden">
          <div className="relative bg-gradient-to-br from-[#12466F] via-[#0F5A89] to-[#0A84FF] text-white px-6 py-8">
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex items-center gap-4">
              <Avatar className="w-20 h-20 rounded-2xl border-2 border-white/40 shadow-lg">
                {form.picture && <AvatarImage src={form.picture} />}
                <AvatarFallback className="rounded-2xl bg-white/20 text-white text-2xl font-bold">
                  {(user.name || user.email)[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 font-semibold">Il tuo account</div>
                <div className="font-display font-black text-3xl leading-tight mt-1 truncate">{user.name || user.email}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge className={`rounded-full text-[10px] uppercase tracking-widest border-0 ${statusBadge.cls}`} data-testid="account-status">
                    {statusBadge.label}
                  </Badge>
                  {subscription?.plan && (
                    <Badge className="rounded-full bg-white/20 backdrop-blur text-white text-[10px] uppercase tracking-widest border border-white/40">
                      <Crown className="w-3 h-3 mr-1" /> {subscription.plan.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription card */}
        <div className="ios-card p-6" data-testid="account-subscription">          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Abbonamento</div>
              <div className="font-display font-bold text-xl mt-1">
                {subscription?.plan?.name || (user.status === "subscriber" ? "Abbonato" : "Modalità Demo")}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {user.status === "subscriber"
                  ? "Il tuo account ha accesso completo alle funzioni premium."
                  : "Passa a un piano premium per sbloccare tutte le funzioni."}
              </p>
              {subscription?.subscription_expires_at && user.status === "subscriber" && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Scadenza</span>
                  <span className="text-sm font-semibold text-slate-800 font-mono-num" data-testid="account-expiry">
                    {new Date(subscription.subscription_expires_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                  </span>
                  {subscription.billing_cycle && (
                    <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-1.5 py-0.5 bg-[#0A84FF]/10 text-[#0A84FF]">
                      {subscription.billing_cycle === "yearly" ? "Annuale" : "Mensile"}
                    </span>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={() => navigate("/pricing")}
              className="rounded-full bg-gradient-to-r from-[#0A84FF] to-[#0060df] text-white h-10 px-5"
              data-testid="account-view-plans"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Vedi i piani <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Communities shortcut — quick access to owned/joined communities.
            Full management (create/customize/invite) lives on the Community page. */}
        <div className="ios-card p-6" data-testid="account-communities">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><Users className="w-3 h-3 inline mr-1" /> Le mie community</div>
              <div className="font-display font-bold text-xl mt-1">Gestisci le tue bolle</div>
              <p className="text-sm text-slate-600 mt-1">Crea, personalizza e sposta la conversazione tra community diverse dalla pagina Community.</p>
            </div>
            <Button
              onClick={() => navigate("/community")}
              className="rounded-full h-10 px-5"
              style={{ background: "#12466F", color: "white" }}
              data-testid="account-open-communities"
            >
              <Users className="w-4 h-4 mr-1.5" /> Vai alle community <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Profile edit */}
        <div className="ios-card p-6 space-y-4" data-testid="account-profile">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dati personali</div>
            {missingFields.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5" data-testid="account-missing-fields">
                {missingFields.length} campi da completare
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nome *</Label>
              <div className="relative mt-1">
                <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="account-first-name" value={form.first_name} onChange={setField("first_name")} placeholder="Marco" className="rounded-xl pl-9 bg-slate-50 border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Cognome *</Label>
              <div className="relative mt-1">
                <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="account-last-name" value={form.last_name} onChange={setField("last_name")} placeholder="Rossi" className="rounded-xl pl-9 bg-slate-50 border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={user.email} disabled className="rounded-xl pl-9 bg-slate-100 text-slate-500" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Cellulare *</Label>
              <div className="relative mt-1">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="account-phone" value={form.phone} onChange={setField("phone")} placeholder="+39 333 1234567" className="rounded-xl pl-9 bg-slate-50 border-slate-200 font-mono-num" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Indirizzo *</Label>
              <div className="relative mt-1">
                <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="account-address" value={form.address} onChange={setField("address")} placeholder="Via Roma 12" className="rounded-xl pl-9 bg-slate-50 border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">CAP *</Label>
              <Input data-testid="account-cap" value={form.cap} onChange={setField("cap")} placeholder="00100" className="rounded-xl mt-1 bg-slate-50 border-slate-200 font-mono-num" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Città *</Label>
              <Input data-testid="account-city" value={form.city} onChange={setField("city")} placeholder="Roma" className="rounded-xl mt-1 bg-slate-50 border-slate-200" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider">URL immagine profilo (opzionale)</Label>
              <Input value={form.picture} onChange={setField("picture")} placeholder="https://…" className="rounded-xl mt-1 bg-slate-50 border-slate-200" data-testid="account-picture" />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] text-slate-500">I campi con <b>*</b> sono richiesti per legge (GDPR).</p>
            <Button onClick={saveProfile} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="account-save-profile">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvo…</> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
            </Button>
          </div>
        </div>

        {/* Change password */}
        <div className="ios-card p-6 space-y-4" data-testid="account-password">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sicurezza</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Password attuale</Label>
              <div className="relative mt-1">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="rounded-xl pl-9 bg-slate-50 border-slate-200" data-testid="account-curpw" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nuova password (min. 8)</Label>
              <div className="relative mt-1">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="rounded-xl pl-9 bg-slate-50 border-slate-200" data-testid="account-newpw" />
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={changePw} disabled={pwSaving || !curPw || !newPw} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="account-change-pw">
              {pwSaving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aggiorno…</> : "Aggiorna password"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">Se hai effettuato l'accesso con Google, imposta una password usando il flusso "Password dimenticata".</p>
        </div>
      </div>
    </div>
  );
};

export default Account;
